import { Mail, PauseCircle, PlayCircle, StopCircle } from 'lucide-react'
import { cn } from '@/lib/utils'
import { useUIStore } from '@/stores/useUIStore'
import { useVoicemailPlayerStore } from '@/stores/useVoicemailPlayerStore'

/**
 * Mini-player audio fixed en bas de l'écran, visible quand un voicemail est en cours de lecture.
 * Connecté à useVoicemailPlayerStore.
 */
export function VoicemailMiniPlayer() {
  const darkMode = useUIStore((s) => s.darkMode)
  const playingVoicemailId = useVoicemailPlayerStore((s) => s.playingVoicemailId)
  const audioElement = useVoicemailPlayerStore((s) => s.audioElement)
  const audioProgress = useVoicemailPlayerStore((s) => s.audioProgress)
  const audioDuration = useVoicemailPlayerStore((s) => s.audioDuration)
  const pause = useVoicemailPlayerStore((s) => s.pause)
  const resume = useVoicemailPlayerStore((s) => s.resume)
  const stop = useVoicemailPlayerStore((s) => s.stop)

  if (!playingVoicemailId || !audioElement) return null

  const elapsed = Math.floor((audioProgress / 100) * audioDuration)

  return (
    <div
      className={cn(
        'fixed bottom-16 md:bottom-0 left-0 right-0 border-t shadow-lg z-40 md:left-[68px] md:lg:left-64',
        darkMode ? 'bg-slate-800 border-slate-700' : 'bg-white border-slate-200',
      )}
    >
      <div className="max-w-7xl mx-auto px-4 py-3">
        <div className="flex items-center gap-3">
          <div
            className={cn(
              'w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0',
              darkMode ? 'bg-slate-700' : 'bg-orange-100',
            )}
          >
            <Mail
              className={cn('h-5 w-5', darkMode ? 'text-orange-400' : 'text-orange-600')}
            />
          </div>

          <div className="flex-1 min-w-0">
            <p
              className={cn(
                'text-sm font-medium truncate',
                darkMode ? 'text-white' : 'text-slate-900',
              )}
            >
              Message vocal
            </p>
            <div className="flex items-center gap-2 mt-1">
              <div
                className={cn(
                  'flex-1 rounded-full h-1.5 overflow-hidden',
                  darkMode ? 'bg-slate-700' : 'bg-slate-200',
                )}
              >
                <div
                  className="h-full bg-indigo-600 transition-all duration-200"
                  style={{ width: `${audioProgress}%` }}
                />
              </div>
              <span
                className={cn(
                  'text-xs whitespace-nowrap',
                  darkMode ? 'text-slate-400' : 'text-slate-500',
                )}
              >
                {elapsed}s / {Math.floor(audioDuration)}s
              </span>
            </div>
          </div>

          <div className="flex items-center gap-2 flex-shrink-0">
            {audioElement.paused ? (
              <button
                type="button"
                onClick={resume}
                className="w-9 h-9 flex items-center justify-center text-white bg-indigo-600 hover:bg-indigo-700 rounded-full transition-colors"
                aria-label="Reprendre"
              >
                <PlayCircle className="w-5 h-5" />
              </button>
            ) : (
              <button
                type="button"
                onClick={pause}
                className="w-9 h-9 flex items-center justify-center text-white bg-orange-600 hover:bg-orange-700 rounded-full transition-colors"
                aria-label="Pause"
              >
                <PauseCircle className="w-5 h-5" />
              </button>
            )}
            <button
              type="button"
              onClick={stop}
              className={cn(
                'w-9 h-9 flex items-center justify-center rounded-full transition-colors',
                darkMode
                  ? 'bg-slate-700 hover:bg-slate-600 text-slate-300'
                  : 'bg-slate-200 hover:bg-slate-300 text-slate-700',
              )}
              aria-label="Fermer"
            >
              <StopCircle className="w-5 h-5" />
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}
